import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import FileV2 from 'src/app/services/file/file-v2.model';
import { FileV2Service } from 'src/app/services/file/file-v2.service';
import { environment } from 'src/environments/environment';

/**
 * Stable, shareable page for one FileV2 document (route file/:id).
 * The API returns a signed content URL that expires after a few minutes, so this page asks for a fresh one
 * every time it opens or reloads and shows the document in a full-window frame. The address bar keeps the
 * stable route, which is what users copy or bookmark; the short-lived URL stays inside the frame.
 */
@Component({
  selector: 'app-file-viewer',
  templateUrl: './file-viewer.component.html',
  styleUrls: ['./file-viewer.component.scss']
})
export class FileViewerComponent implements OnInit, OnDestroy {

  src?: SafeResourceUrl;
  fileName = '';
  error?: string;
  private subscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private fileV2Service: FileV2Service,
    private sanitizer: DomSanitizer,
    private title: Title
  ) { }

  ngOnInit(): void {
    // A deep link that went through login stored this route; clear it so the next login does not come back here.
    if (localStorage.getItem('redirect-route') === `/file/${this.route.snapshot.paramMap.get('id')}`) {
      localStorage.removeItem('redirect-route');
    }

    this.subscription = this.route.paramMap.subscribe(params => {
      const id = Number(params.get('id'));
      this.src = undefined;
      this.error = undefined;
      if (!id) {
        this.error = 'File not found.';
        return;
      }
      this.load(id);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.title.setTitle(environment.name);
  }

  /**
   * Requests the file record, whose url is a freshly signed content URL, and frames it.
   */
  private load(id: number) {
    this.fileV2Service.obtainFile(id).subscribe({
      next: (file: FileV2) => {
        if (!file?.url) {
          this.error = 'File not found.';
          return;
        }
        this.fileName = file.name;
        this.title.setTitle(file.name);
        this.src = this.sanitizer.bypassSecurityTrustResourceUrl(file.url);
      },
      error: (err) => {
        this.error = err?.status === 404 ? 'File not found.' : 'The file could not be opened.';
      }
    });
  }
}
